import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import qs from 'query-string'
import { useForm } from '@mantine/form'
import { Button, Grid, Group, LoadingOverlay, MediaQuery, Pagination, Select, Table, TextInput } from '@mantine/core'
import { Search as SearchIcon, SquarePlus as SquarePlusIcon } from 'tabler-icons-react'
import { MainLayout } from '../../layouts'
import { useAppDispatch, useAppSelector } from '../../../store'
import { IClassesQueryParams } from '../../../store/types'
import { DEFAULT_PER_PAGE, SELECT_PER_PAGE_OPTIONS } from '../../../types'
import { fetchListClasses } from '../../../store/features/cls/listClassSlice'
import { fetchDeleteClass, resetDeleteClass } from '../../../store/features/cls/deleteClassSlice'
import { ContentBox, ContentNotFound, ItemActionsMenu } from '../../partials'
import { showNotification } from '@mantine/notifications'
const List = () => {
  const { t } = useTranslation()
  const { search } = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [queryParams, setQueryParams] = useState<IClassesQueryParams>({
    page: 1,
    per: DEFAULT_PER_PAGE,
    ...qs.parse(search)
  })
  const {
    isLoading: deleteIsLoading,
    response: deleteResponse,
    error: deleteError
  } = useAppSelector(state => state.deleteClass)
  const { isLoading, response, error } = useAppSelector(state => state.listClass)
  const filterForm = useForm({
    initialValues: {
      q: queryParams.q || '',
      per: queryParams.per
    }
  })

  const handleFilterSubmit = async (data: IClassesQueryParams) => {
    setQueryParams({ ...queryParams, ...data, page: 1 })
    navigate(`/classes?${qs.stringify(data)}`, { replace: true })
  }

  useEffect(() => {
    dispatch(fetchListClasses({ query: queryParams }))
  }, [dispatch, queryParams])

  useEffect(() => {
    if (error !== null && error.response.status === 401) {
      dispatch(fetchListClasses({ query: queryParams }))
    }
  }, [error, dispatch, queryParams])

  const handleChangePage = (page: number) => {
    if (page !== Number(queryParams.page)) {
      setQueryParams({ ...queryParams, page })
      navigate(`/classes?${qs.stringify({ ...queryParams, page })}`, { replace: true })
    }
  }

  const handleDelete = async (id: string) => {
    dispatch(fetchDeleteClass(id))
  }

  useEffect(() => {
    if (deleteResponse !== null) {
      dispatch(resetDeleteClass())

      showNotification({
        color: 'green',
        title: t('successful'),
        message: t('deleted')
      })

      setQueryParams({ ...queryParams, page: 1 })
      navigate(`/classes?${qs.stringify({ ...queryParams, page: 1 })}`, { replace: true })
    }
  }, [deleteResponse, queryParams, t, dispatch, navigate])

  useEffect(() => {
    if (deleteError !== null) {
      dispatch(resetDeleteClass())

      showNotification({
        color: 'red',
        title: t('successful'),
        message: deleteError.response ? deleteError.response.message : t('errors.unknown')
      })
    }
  }, [deleteError, t, dispatch])

  return (
    <MainLayout>
      <LoadingOverlay visible={isLoading || deleteIsLoading} />
      {response && (
        <MediaQuery
          largerThan="md"
          styles={{ flexDirection: 'row-reverse' }}
        >
          <Grid gutter="sm">
            <Grid.Col
              lg={3}
              md={4}
              sm={12}
            >
              <ContentBox title={t('filter')}>
                <form onSubmit={filterForm.onSubmit(handleFilterSubmit)}>
                  <TextInput
                    placeholder={t('keywords')}
                    label={t('keywords')}
                    mb="xs"
                    {...filterForm.getInputProps('q')}
                  />
                  <Select
                    data={SELECT_PER_PAGE_OPTIONS}
                    label={t('per_page')}
                    placeholder={t('select')}
                    mb="xs"
                    {...filterForm.getInputProps('per')}
                  />
                  <Button
                    type="submit"
                    leftIcon={<SearchIcon size={16} />}
                    variant="outline"
                    color="cyan"
                    fullWidth
                  >
                    {t('filter')}
                  </Button>
                </form>
              </ContentBox>
            </Grid.Col>
            <Grid.Col
              lg={9}
              md={8}
              sm={12}
            >
              <ContentBox
                title="Classes"
                subtitle={`${t('total')}: ${response.data.count}`}
                withoutSpacing
              >
                {response.data.items.length > 0 ? (
                  <div className="table-responsive">
                    <Table
                      striped
                      highlightOnHover
                    >
                      <thead>
                        <tr>
                          <th>{t('name')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {response.data.items.map(({ id, name }) => (
                          <tr key={id}>
                            <td>{name}</td>

                            <td className="text-center">
                              <ItemActionsMenu
                                show={`/classes/${id}`}
                                edit={`/classes/${id}/edit`}
                                onDelete={() => handleDelete(id)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <ContentNotFound />
                )}
              </ContentBox>
              <ContentBox>
                <Grid>
                  <Grid.Col span={8}>
                    {response.data.pagination.last > 1 && (
                      <Pagination
                        page={Number(queryParams.page)}
                        onChange={handleChangePage}
                        total={response.data.pagination.last}
                      />
                    )}
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Group position="right">
                      <Button
                        leftIcon={<SquarePlusIcon size={16} />}
                        component={Link}
                        to="/classes/create"
                        variant="outline"
                        color="green"
                      >
                        {t('content_actions.create')}
                      </Button>
                    </Group>
                  </Grid.Col>
                </Grid>
              </ContentBox>
            </Grid.Col>
          </Grid>
        </MediaQuery>
      )}
    </MainLayout>
  )
}

export default List
